import Button from '@/common/components/Button'
import MaskedTextField from '@/common/components/MaskedTextField'
import Modal from '@/common/components/Modal'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import { cnpjCpfMask } from '@/common/constants/masks.constant'
import { useBankInstitutions } from '@/common/hooks/queries/useBankInstitutions'
import useDebounce from '@/common/hooks/useDebounce'
import { BankInstitutionsTransformer } from '@/common/services/bankInstitutionsTransformer'
import { Mapper } from '@/common/services/mapper'
import { BankAccountInformation } from '@/common/types/establishment/company.type'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useUpdateEstablishmentCompanyBranch } from '@/features/establishments/hooks/useUpdateEstablishmentCompanyBranch'
import {
  updateEstablishmentCompanyBranchSchema,
  UpdateEstablishmentCompanyBranchValidationSchema,
} from '@/features/establishments/schemas/updateEstablishmentCompanyBranch.schema'
import { updateEstablishmentCompanyBranchDefaultValues } from '@/features/establishments/utils/updateEstablishmentCompanyBranchDefaultValues'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface BankAccountUpdateModalProps {
  establishmentCompanyId?: string
  establishmentCompanyBranchId?: string
  bankAccountInformation?: BankAccountInformation
  establishmentCompanyIsLoading?: boolean
}

export const BankAccountUpdateModal = ({
  establishmentCompanyId,
  establishmentCompanyBranchId,
  bankAccountInformation,
  establishmentCompanyIsLoading,
}: BankAccountUpdateModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [searchBankInstitutions, setSearchBankInstitutions] = useDebounce('')

  const { data: bankInstitutionOptions } = useBankInstitutions({
    filters: { search: searchBankInstitutions },
    mapper: Mapper.mapWithTransformer({ transformer: BankInstitutionsTransformer.optionsTransformer() }),
  })

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<UpdateEstablishmentCompanyBranchValidationSchema>({
    resolver: zodResolver(updateEstablishmentCompanyBranchSchema),
    defaultValues: bankAccountInformation ? updateEstablishmentCompanyBranchDefaultValues(bankAccountInformation) : {},
  })

  const { mutate: updateEstablishmentCompanyBranch, isPending: updateEstablishmentCompanyBranchIsPending } =
    useUpdateEstablishmentCompanyBranch({
      establishmentCompanyBranchId: establishmentCompanyBranchId,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompanyId] })
        setIsOpen(false)
      },
      onError: FormTransformers.errorsTransformer<UpdateEstablishmentCompanyBranchValidationSchema>({ setError }),
    })

  const onSubmit: SubmitHandler<UpdateEstablishmentCompanyBranchValidationSchema> = (data) =>
    updateEstablishmentCompanyBranch(data)

  useEffect(() => {
    if (!establishmentCompanyIsLoading && bankAccountInformation) {
      reset(updateEstablishmentCompanyBranchDefaultValues(bankAccountInformation))
    }
  }, [establishmentCompanyIsLoading])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      Trigger={<Button operationKlassToCheckPermission='Admin::Establishment::CompanyBranches::Update'>Editar</Button>}
      triggerDisabled={establishmentCompanyIsLoading}
      useCancellableFooter
      showClose={false}
      title="Detalhes bancários"
      className="min-w-[720px]"
      subtitleClassName="mt-1 text-md text-white-300"
      cancelButtonClassName="bg-white-200 rounded-es-lg"
      confirmButtonClassName="bg-blue-700 rounded-ee-lg"
      disabled={updateEstablishmentCompanyBranchIsPending}
      confirmButtonIsLoading={updateEstablishmentCompanyBranchIsPending}
      onCancel={reset}
      onConfirm={handleSubmit(onSubmit)}
      body={
        <form>
          <div className="flex flex-col gap-4">
            <SelectController
              label="Banco"
              markAsRequired
              name="bank_account_information.bank_institution"
              control={control}
              options={bankInstitutionOptions}
              onInputChange={(value) => setSearchBankInstitutions(value)}
              errorMessage={errors?.bank_account_information?.bank_institution?.message}
              isDisabled={updateEstablishmentCompanyBranchIsPending}
            />

            <div className="flex gap-2">
              <TextField
                label="Agência (com dígito)"
                markAsRequired
                className="grow"
                {...register('bank_account_information.agency')}
                errorMessage={errors?.bank_account_information?.agency?.message || undefined}
                disabled={updateEstablishmentCompanyBranchIsPending}
              />

              <TextField
                label="Conta (com dígito)"
                markAsRequired
                className="grow"
                {...register('bank_account_information.account')}
                errorMessage={errors?.bank_account_information?.account?.message || undefined}
                disabled={updateEstablishmentCompanyBranchIsPending}
              />
            </div>

            <div className="flex gap-2">
              <TextField
                className='w-1/2'
                label="Nome do favorecido"
                markAsRequired
                {...register('bank_account_information.payee_name')}
                errorMessage={errors?.bank_account_information?.payee_name?.message || undefined}
                disabled={updateEstablishmentCompanyBranchIsPending}
              />

              <MaskedTextField
                className='w-1/2'
                label="CNPJ/CPF do favorecido"
                mask={cnpjCpfMask}
                errorMessage={errors?.bank_account_information?.payee_cnpj?.message || undefined}
                {...register('bank_account_information.payee_cnpj')}
                disabled={updateEstablishmentCompanyBranchIsPending}
              />
            </div>
          </div>
        </form>
      }
    />
  )
}
